import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useAuth } from "../auth/AuthContext";
import {
  createProject as createProjectDoc,
  deleteProject as deleteProjectDoc,
  fetchProjects,
  updateProject as updateProjectDoc,
} from "./api";
import reducer, {
  addProject,
  removeProject,
  setProjects,
  editProject,
} from "./reducer";

export type Project = {
  name: string;
  description: string;
  notes?: string;
  componentSkills: string[];
};

type Entity = {
  id: string;
};

export type ProjectEntity = Project & Entity;

export type Projects = {
  [id: string]: ProjectEntity;
};

type ProjectsContextState = {
  createProject: (project: Project) => Promise<string>;
  deleteProject: (projectId: string) => Promise<void>;
  getProject: (id: string) => ProjectEntity | undefined;
  updateProject: (projectId: string, updatedFields: Partial<Project>) => void;
  projects: Projects;
  projectSkillSelections: string[];
  addProjectComponentSkills: (
    projectId: string,
    skillIds: string[],
  ) => Promise<void>;
  removeProjectComponentSkill: (
    projectId: string,
    ...ids: string[]
  ) => Promise<void>;
  addProjectSkillSelection: (id: string) => void;
  removeProjectSkillSelection: (id: string) => void;
  clearProjectSkillSelections: () => void;
  projectForDeletion: string | null;
  stageProjectForDeletion: (id: string) => void;
  clearStagedProjectForDeletion: () => void;
};

export const ProjectsContext = createContext<ProjectsContextState>({
  createProject: () => Promise.resolve(""),
  deleteProject: () => Promise.resolve(),
  getProject: () => undefined,
  updateProject: () => Promise.resolve(),
  projects: {},
  projectSkillSelections: [],
  addProjectComponentSkills: () => Promise.resolve(),
  removeProjectComponentSkill: () => Promise.resolve(),
  addProjectSkillSelection: () => undefined,
  removeProjectSkillSelection: () => undefined,
  clearProjectSkillSelections: () => undefined,
  projectForDeletion: null,
  stageProjectForDeletion: () => undefined,
  clearStagedProjectForDeletion: () => undefined,
});

type ProjectsProviderProps = {
  children: ReactNode;
};

const ProjectsProvider = ({ children }: ProjectsProviderProps) => {
  const { user } = useAuth();
  const [projects, dispatch] = useReducer(reducer, {});
  const [projectForDeletion, setProjectForDeletion] = useState<string | null>(
    null,
  );
  const [selectedProjectComponentSkills, setSelectedProjectComponentSkills] =
    useState<string[]>([]);

  const getProject = (id: string) => {
    return projects[id];
  };

  const updateProject = async (
    projectId: string,
    updatedFields: Partial<Project>,
  ) => {
    if (!user) {
      throw Error("User not logged in!");
    }
    await updateProjectDoc(user.uid, projectId, updatedFields);
    dispatch(editProject(projectId, updatedFields));
  };

  const addProjectSkillSelection = (id: string) => {
    setSelectedProjectComponentSkills((prev) => [...prev, id]);
  };
  const removeProjectSkillSelection = (id: string) => {
    setSelectedProjectComponentSkills((prev) =>
      prev.filter((skill) => skill !== id),
    );
  };
  const clearProjectSkillSelections = () => {
    setSelectedProjectComponentSkills([]);
  };

  const createProject = async (projectData: Project) => {
    if (!user) {
      throw Error("User not logged in!");
    }
    try {
      const id = await createProjectDoc(user.uid, projectData);
      dispatch(addProject({ ...projectData, id }));
      return id;
    } catch (e) {
      throw e;
    }
  };

  const stageProjectForDeletion = (id: string) => {
    setProjectForDeletion(id);
  };
  const clearStagedProjectForDeletion = () => {
    setProjectForDeletion(null);
  };

  const deleteProject = async (projectId: string) => {
    if (!user) {
      throw Error("User not logged in!");
    }
    try {
      await deleteProjectDoc(user.uid, projectId);
      dispatch(removeProject(projectId));
    } catch (e) {
      throw e;
    }
  };

  const removeProjectComponentSkill = async (
    projectId: string,
    ...skillIds: string[]
  ) => {
    if (!user) {
      throw Error("User not logged in!");
    }
    const projectSkills = projects[projectId].componentSkills;
    const updatedComponentSkills = projectSkills.filter(
      (skill) => !skillIds.includes(skill),
    );
    try {
      await updateProjectDoc(user.uid, projectId, {
        componentSkills: updatedComponentSkills,
      });
      dispatch(
        editProject(projectId, { componentSkills: updatedComponentSkills }),
      );
    } catch (e) {
      throw e;
    }
  };

  const addProjectComponentSkills = async (
    projectId: string,
    skillIds: string[],
  ) => {
    if (!user) {
      throw Error("User not logged in!");
    }
    const originalComponentSkills = projects[projectId].componentSkills;
    const updatedComponentSkills = Array.from(
      new Set(originalComponentSkills.concat(skillIds)),
    );

    try {
      await updateProjectDoc(user.uid, projectId, {
        componentSkills: updatedComponentSkills,
      });
      dispatch(
        editProject(projectId, { componentSkills: updatedComponentSkills }),
      );
    } catch (e) {
      throw e;
    }
  };

  useEffect(() => {
    (async () => {
      if (user) {
        const userProjects = await fetchProjects(user.uid);
        dispatch(setProjects(userProjects));
      } else {
        dispatch(setProjects([]));
      }
    })();
  }, [user]);

  return (
    <ProjectsContext.Provider
      value={{
        createProject,
        deleteProject,
        getProject,
        updateProject,
        projects,
        projectSkillSelections: selectedProjectComponentSkills,
        addProjectComponentSkills,
        removeProjectComponentSkill,
        addProjectSkillSelection,
        removeProjectSkillSelection,
        clearProjectSkillSelections,
        projectForDeletion,
        stageProjectForDeletion,
        clearStagedProjectForDeletion,
      }}
    >
      {children}
    </ProjectsContext.Provider>
  );
};

export default ProjectsProvider;

export const useProjects = () => {
  return useContext(ProjectsContext);
};
